import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface SerialDetail {
  id: string;
  variantId: string;
  serialNo: string;
  status: string;
  currentWarehouseId: string | null;
  currentWarehouseName: string | null;
  supplierImportId: string | null;
  importedAt: string;
  soldAt: string | null;
  warrantyStart: string | null;
  warrantyEnd: string | null;
  note: string;
  variantSku: string | null;
  productName: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  imported: "Nhập về",
  in_stock: "Trong kho",
  sold: "Đã bán",
  in_repair: "Đang sửa",
  rma_to_vendor: "RMA nhà cung cấp",
  defective: "Hỏng",
  returned: "Hoàn trả",
};

const STATUS_COLORS: Record<string, string> = {
  in_stock: "bg-green-100 text-green-700",
  sold: "bg-blue-100 text-blue-700",
  in_repair: "bg-yellow-100 text-yellow-700",
  rma_to_vendor: "bg-orange-100 text-orange-700",
  defective: "bg-red-100 text-red-700",
  returned: "bg-purple-100 text-purple-700",
  imported: "bg-muted text-muted-foreground",
};

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("vi-VN");
}

export function SerialLookupPage() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");

  const { data: serial, isLoading, isFetched } = useQuery({
    queryKey: ["serial", query],
    queryFn: () =>
      apiClient
        .get<{ data: SerialDetail }>(`/v1/serials/${encodeURIComponent(query)}`)
        .then((r) => r.data.data),
    enabled: query.trim().length > 0,
    retry: false,
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQuery(input.trim());
  }

  const notFound = isFetched && !serial && !isLoading;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Tra cứu serial</h1>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Nhập serial number…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={!input.trim()}>
          Tra cứu
        </Button>
      </form>

      {isLoading && <p className="text-muted-foreground">Đang tìm…</p>}

      {notFound && (
        <p className="text-destructive">
          Không tìm thấy serial <strong>{query}</strong>
        </p>
      )}

      {serial && (
        <Card className="max-w-lg p-6 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Serial</p>
              <p className="font-mono text-lg font-semibold">{serial.serialNo}</p>
            </div>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[serial.status] ?? "bg-muted text-muted-foreground"}`}
            >
              {STATUS_LABELS[serial.status] ?? serial.status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <p className="text-muted-foreground">Sản phẩm</p>
              <p className="font-medium">{serial.productName ?? "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">SKU</p>
              <p className="font-mono">{serial.variantSku ?? "—"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-muted-foreground">Kho hiện tại</p>
              <p className="font-medium">{serial.currentWarehouseName ?? "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Ngày nhập</p>
              <p>{fmt(serial.importedAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Ngày bán</p>
              <p>{fmt(serial.soldAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Bảo hành từ</p>
              <p>{fmt(serial.warrantyStart)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Bảo hành đến</p>
              <p>{fmt(serial.warrantyEnd)}</p>
            </div>
          </div>

          {serial.note && (
            <div className="text-sm">
              <p className="text-muted-foreground">Ghi chú</p>
              <p>{serial.note}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
